<template>
	<Button
		v-if="analysis.draft ? analysis.canWrite : canSelect"
		class="select-btn"
		size="s"
		:variant="isAddedToAnalysis ? 'primary' : 'secondary'"
		:disabled="disabled"
		:title="
			isExistingComparable
				? t('comparableAnalysis.alreadyInAnalysis')
				: isAddedToAnalysis
					? t('comparableAnalysis.removeFromAnalysis')
					: undefined
		"
		@click.stop="toggle"
	>
		<AppIcon v-if="isAddedToAnalysis" :name="IconName.Check" class="button-icon" :size="12" :stroke-width="2" />
		{{ label }}
	</Button>
</template>
<script setup lang="ts">
import { computed, toRaw } from "vue"
import { useI18n } from "vue-i18n"
import type { Transaction } from "@/model/DataModel"
import { UseRootStore } from "@/model/RootStore"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"
import { summarizeComparableTransaction } from "@/utils/comparableTransactionSummary"
import { normalizeComparableInscriptionNumber, useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"

const props = defineProps<{ transaction: Transaction; canSelect: boolean }>()
const emit = defineEmits<{ (event: "export-selection-changed"): void }>()
const root = UseRootStore()
const analysis = useComparableAnalysisStore()
const { t } = useI18n()
const number = computed(() => normalizeComparableInscriptionNumber(props.transaction.inscriptionNumber ?? ""))
const selected = computed(() =>
	analysis.draft
		? analysis.draft.input.comparableInscriptionNumbers.includes(number.value)
		: root.transactionExportList.has(props.transaction.presentationId)
)
const isExistingComparable = computed(() => selected.value && analysis.isExistingComparable(number.value))
const isAddedToAnalysis = computed(() => !!analysis.draft && selected.value)
const isAnalysisFull = computed(
	() => !!analysis.draft && !selected.value && analysis.draft.input.comparableInscriptionNumbers.length >= 50
)
const label = computed(() => {
	if (isExistingComparable.value) return t("comparableAnalysis.alreadyInAnalysis")
	if (isAddedToAnalysis.value) return t("comparableAnalysis.added")
	if (isAnalysisFull.value) return t("comparableAnalysis.limitReached")
	return t(selected.value ? "transaction.card.deselectTransaction" : "transaction.card.selectTransaction")
})
const disabled = computed(
	() =>
		!!analysis.draft &&
		(!/^\d+$/.test(number.value) || analysis.saving || isAnalysisFull.value || isExistingComparable.value)
)
function toggle() {
	if (disabled.value) return
	if (analysis.draft) {
		if (!analysis.canWrite) return
		if (selected.value) analysis.removeTransaction(number.value)
		else analysis.addTransaction(number.value, summarizeComparableTransaction(props.transaction))
		return
	}
	if (!props.canSelect) return
	const transaction = toRaw(props.transaction)
	if (transaction.presentationId == null) return
	if (selected.value) root.transactionExportList.delete(transaction.presentationId)
	else root.transactionExportList.set(transaction.presentationId, transaction)
	emit("export-selection-changed")
}
</script>
<style scoped>
.button-icon {
	margin-right: var(--spacing-xs);
}
</style>
